import { useEffect, useRef, useState } from "react";
import TableHoverCard from "./TableHoverCard";

const HOVER_CLOSE_DELAY_MS = 180;

const AssetIdHoverCell = ({
  index,
  label,
  onClick,
  children,
  defaultCardWidth = 500,
  defaultCardHeight = 430,
  zIndex = 10000,
}) => {
  const [showCard, setShowCard] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [cardSize, setCardSize] = useState({
    width: defaultCardWidth,
    height: defaultCardHeight,
  });
  const hoverTimeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);

  const clearHoverTimeout = () => {
    if (!hoverTimeoutRef.current) return;
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  };

  const syncAnchorRect = () => {
    if (!triggerRef.current) return;
    setAnchorRect(triggerRef.current.getBoundingClientRect());
  };

  const openCard = () => {
    clearHoverTimeout();
    syncAnchorRect();
    setShowCard(true);
  };

  const closeCard = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setShowCard(false);
    }, HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearHoverTimeout(), []);

  useEffect(() => {
    setCardSize({
      width: defaultCardWidth,
      height: defaultCardHeight,
    });
  }, [defaultCardHeight, defaultCardWidth]);

  useEffect(() => {
    if (!showCard) return undefined;

    const handleViewportChange = () => syncAnchorRect();

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [showCard]);

  useEffect(() => {
    if (!showCard || !cardRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const updateCardSize = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      setCardSize((previous) => {
        const nextWidth = Math.round(rect.width);
        const nextHeight = Math.round(rect.height);

        if (previous.width === nextWidth && previous.height === nextHeight) {
          return previous;
        }

        return { width: nextWidth, height: nextHeight };
      });
    };

    updateCardSize();

    const resizeObserver = new ResizeObserver(() => {
      updateCardSize();
    });

    resizeObserver.observe(cardRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [showCard]);

  return (
    <div
      ref={triggerRef}
      className="vehicle_hover_cell"
      onMouseEnter={openCard}
      onMouseLeave={closeCard}
      style={{ display: "inline-block", position: "relative", overflow: "visible" }}
    >
      <span
        className="hyperlink vehicle_hover_link"
        style={{ cursor: "pointer" }}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
      >
        {label}
      </span>

      <TableHoverCard
        show={showCard}
        index={index}
        placement="right"
        cardWidth={cardSize.width}
        popupHeight={cardSize.height}
        offsetX={14}
        offsetY={0}
        anchorRect={anchorRect}
        portalToBody
        viewportPadding={16}
        zIndex={zIndex}
        onMouseEnter={openCard}
        onMouseLeave={closeCard}
      >
        <div ref={cardRef}>{children}</div>
      </TableHoverCard>
    </div>
  );
};

export default AssetIdHoverCell;